import { useMemo, useState, type JSX } from 'react';
import { FieldValues } from 'react-hook-form';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

import { FormLabelWrapper, TextInput, Button } from '@restworld/ui-ds-base';
import { Wrapper } from '@restworld/ui-components';
import { useAnalytics } from '@restworld/utils-common';

import { InformationHeadLine } from './error-info-headline';
import { ErrorWrapper, InvalidFormErrors, missingFieldFormLoadingAtom } from './cv-errors-field-reinput';
import { workerEntryPointErrorsAtom } from '../worker-entry-point.atoms';
import { buildLoginUrlWithApplyIntent } from '../apply-intent';
import { loginClickedProperties } from '../existing-account-events';
import { useJobPositionId } from '../../../hooks';

export const ExistingEmailInput = ({
  formInputs,
  invalidFormErrors,
}: {
  formInputs?: JSX.Element[];
  invalidFormErrors?: FieldValues;
}) => {
  const errors = useAtomValue(workerEntryPointErrorsAtom);
  const loading = useAtomValue(missingFieldFormLoadingAtom);
  const existingEmail = errors?.email?.split(' ')[0];

  const missingFields = useMemo(
    () => (errors ? Object.keys(errors).filter((v) => !['user_id', 'statusCode', 'id'].includes(v)) : []),
    [errors]
  );
  const [isUpdateEmail, toggleUpdateEmail] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const jobPositionId = useJobPositionId();
  return (
    <ErrorWrapper>
      <InformationHeadLine
        title={
          isUpdateEmail
            ? missingFields.length > 1
              ? t('onboarding-page:update_some_information')
              : t('onboarding-page:update_emails_address')
            : t('onboarding-page:email_already_used_how_do_you_want_to_proceed')
        }
        showBackButton={isUpdateEmail}
        backButtonClickHandler={() => toggleUpdateEmail(false)}
      />
      {isUpdateEmail ? (
        <Wrapper>{formInputs}</Wrapper>
      ) : (
        <FormLabelWrapper label="Email" id="readOnly-email">
          <TextInput
            type="email"
            id="readOnly-email"
            placeholder={existingEmail ?? 'mario@example.com'}
            rightIcon={LockClosedIcon}
            required
            readOnly
            hasLeftPadding={false}
          />
        </FormLabelWrapper>
      )}
      <Wrapper>
        <InvalidFormErrors invalidFormErrors={invalidFormErrors} />
        {isUpdateEmail ? (
          <Button variant="primary" type="submit" loading={loading} fullwidth>
            {missingFields.length > 1 ? t('common:update') : t('onboarding-page:use_this_email_for_the_new_profile')}
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                trackEvent(
                  'existing_account_login_clicked',
                  loginClickedProperties({ jobPositionId, placement: 'post_409', methodHint: 'email' })
                );
                router.push(buildLoginUrlWithApplyIntent(window.location.href, { email: existingEmail }));
              }}
              variant="primary"
              type="button"
              fullwidth
            >
              {t('onboarding-page:log_in_with_this_email')}
            </Button>
            <Button variant="secondary-neutral" onClick={() => toggleUpdateEmail(true)} fullwidth>
              {t('onboarding-page:update_email_and_create_a_new_profile')}
            </Button>
          </>
        )}
      </Wrapper>
    </ErrorWrapper>
  );
};
